import { type UIEvent, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { TabBar } from './TabBar';
import { TenantDrawer } from './TenantDrawer';
import { useUiStore } from '@shared/stores/uiStore';
import { useMemberProfile } from '@shared/hooks/useMemberProfile';
import { useTabBarHidden } from '@shared/hooks/useTabBarHidden';

const HEADER_COLLAPSE_AT = 156;
const HEADER_EXPAND_AT = 132;

/**
 * TabShell — authenticated app frame. There is no persistent top bar:
 * every screen is full-bleed and paints its own header.
 *
 * Page changes are instant — no transition animation. The scroll
 * container is keyed by pathname so each route mounts fresh and scroll
 * resets to the top.
 */
export function TabShell(): JSX.Element {
  const location = useLocation();
  const navDrawerOpen = useUiStore((s) => s.navDrawerOpen);
  const setNavDrawerOpen = useUiStore((s) => s.setNavDrawerOpen);
  const headerCollapsed = useUiStore((s) => s.headerCollapsed);
  const setHeaderCollapsed = useUiStore((s) => s.setHeaderCollapsed);
  const trackPath = useUiStore((s) => s.trackPath);
  const collapsedRef = useRef(headerCollapsed);
  const scrollTopRef = useRef(0);
  const frameRef = useRef<number | null>(null);
  const tabBarHidden = useTabBarHidden();
  useMemberProfile();

  useEffect(() => {
    collapsedRef.current = headerCollapsed;
  }, [headerCollapsed]);

  useLayoutEffect(() => {
    setHeaderCollapsed(false);
    collapsedRef.current = false;
    scrollTopRef.current = 0;
  }, [location.pathname, setHeaderCollapsed]);

  useEffect(() => {
    trackPath(location.pathname);
  }, [location.pathname, trackPath]);

  useEffect(() => {
    return () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      scrollTopRef.current = event.currentTarget.scrollTop;

      if (frameRef.current !== null) return;

      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        const scrollTop = scrollTopRef.current;
        const shouldCollapse = collapsedRef.current
          ? scrollTop > HEADER_EXPAND_AT
          : scrollTop > HEADER_COLLAPSE_AT;

        if (shouldCollapse !== collapsedRef.current) {
          collapsedRef.current = shouldCollapse;
          setHeaderCollapsed(shouldCollapse);
        }
      });
    },
    [setHeaderCollapsed],
  );

  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-paper">
      <main
        key={location.pathname}
        onScroll={handleScroll}
        className={tabBarHidden
          ? 'flex-1 overflow-y-auto overflow-x-hidden overscroll-y-none bg-paper'
          : 'flex-1 overflow-y-auto overflow-x-hidden overscroll-y-none bg-paper pb-24'}
      >
        <Outlet />
      </main>
      {tabBarHidden ? null : <TabBar />}
      <TenantDrawer open={navDrawerOpen} onOpenChange={setNavDrawerOpen} />
    </div>
  );
}
